import React, { memo, useCallback, useState } from 'react';

import { List, ListItem } from '@mui/material';
import SearchInputCDS from 'app/shared/componentsRestyled/coreDesignSystem/SearchInputCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { NAVIGATION_ITEM_TYPE } from 'constants/navigation';

import NavigationMenuItemGroupItem from './NavigationMenuGroupItem';
import { getListStyles, listItemStyles } from './NavigationMenuGroupItems.styles';

const filterNavigation = (navItems, searchValue) =>
  navItems.reduce((acc, cur) => {
    const isMatch = cur?.title?.match(new RegExp(_.escapeRegExp(searchValue), 'gim'));

    if (isMatch) {
      acc.push(cur);

      return acc;
    }

    if (cur?.children) {
      const filteredChildren = filterNavigation(cur.children, searchValue);

      if (filteredChildren?.length) {
        acc.push({ ...cur, children: filteredChildren });
      }
    }

    return acc;
  }, []);

const NavigationMenuGroupItems = ({
  withSearch = false,
  isChildrenExpanded = false,
  isNestedList = false,
  isVertical = false,
  searchRef = null,
  onClose,
  onCloseSidebar,
  ...item
}) => {
  const [filteredNavigation, setFilteredNavigation] = useState(item);
  const [isSearch, setIsSearch] = useState(false);

  const onSearch = useCallback(
    searchText => {
      const isSearch = !!searchText.trim();

      setIsSearch(isSearch);

      if (!isSearch) {
        return setFilteredNavigation(item);
      }

      const filteredChildren = filterNavigation(item.children, searchText.trim());

      setFilteredNavigation({ ...item, children: filteredChildren });
    },
    [item],
  );

  return (
    <List sx={getListStyles(isNestedList)}>
      {withSearch && (
        <ListItem sx={listItemStyles}>
          <SearchInputCDS
            focusOnType
            searchOnType
            ariaLabel="Search menu items"
            autoComplete="off"
            containerClassName="!mb-4"
            ref={searchRef}
            onSearch={onSearch}
          />
        </ListItem>
      )}

      {filteredNavigation?.children?.map(item => {
        const { type, id, children } = item;

        const isGroup = type === NAVIGATION_ITEM_TYPE.GROUP;
        const isCollapse = type === NAVIGATION_ITEM_TYPE.COLLAPSE;
        const isItem = type === NAVIGATION_ITEM_TYPE.ITEM;

        if (isGroup) {
          return (
            <ListItem key={`${id}-${item.children.length}`} sx={listItemStyles}>
              <NavigationMenuGroupItems
                isChildrenExpanded={isChildrenExpanded || isSearch}
                isVertical={isVertical}
                onClose={onClose}
                onCloseSidebar={onCloseSidebar}
                {...item}
              />
            </ListItem>
          );
        }

        if (isItem || isCollapse) {
          return (
            <ListItem key={`${id}-${children?.length}`} sx={listItemStyles}>
              <NavigationMenuItemGroupItem
                isChildrenExpanded={isChildrenExpanded || isSearch}
                isVertical={isVertical}
                onClose={onClose}
                onCloseSidebar={onCloseSidebar}
                {...item}
              />
            </ListItem>
          );
        }

        return null;
      })}
    </List>
  );
};

NavigationMenuGroupItems.propTypes = {
  onClose: PropTypes.func.isRequired,
  withSearch: PropTypes.bool,
  isChildrenExpanded: PropTypes.bool,
  isNestedList: PropTypes.bool,
  isVertical: PropTypes.bool,
  searchRef: PropTypes.object,
  onCloseSidebar: PropTypes.func,
};

export default memo(NavigationMenuGroupItems);
